import React, { createContext, useContext, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

export type RoomMode = "default" | "exam" | "exam-step-2" | "exam-step-3" | "exam-loading" | "add-content";

interface RoomModeContextValue {
  mode: RoomMode;
  setMode: (mode: RoomMode) => void;
}

const RoomModeContext = createContext<RoomModeContextValue | undefined>(
  undefined
);

export const RoomModeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialMode = searchParams.get("mode") as RoomMode | null;
  const [mode, setMode] = useState<RoomMode>(
    initialMode && ["exam", "exam-step-2", "exam-step-3", "exam-loading", "add-content"].includes(initialMode)
      ? initialMode
      : "default"
  );

  // Clear the mode query param after reading it (one-time initialization)
  useEffect(() => {
    if (initialMode) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("mode");
      setSearchParams(newParams, { replace: true });
    }
  }, []);

  return (
    <RoomModeContext.Provider value={{ mode, setMode }}>
      {children}
    </RoomModeContext.Provider>
  );
};

export const useRoomMode = () => {
  const ctx = useContext(RoomModeContext);
  if (!ctx) {
    throw new Error("useRoomMode must be used within RoomModeProvider");
  }
  return ctx;
};
